import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import {
  Database, Download, FileSpreadsheet, RefreshCw, Trash2,
  CheckCircle, AlertCircle, Clock, HardDrive, Table2,
  Users, Truck, ShoppingCart, Package, DollarSign, UserCheck,
  BarChart2, Shield, X, FileText, Printer,
} from "lucide-react";
import PersianDatePicker, { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import SearchableSelect from "../../components/SearchableSelect";
import { warehouseAPI } from "../../services/api";

const API = "https://www.hamidigroupfinance.com/api";
const getToken = () => localStorage.getItem("auth_token") || "";

const post = async (action: string, extra = {}) => {
  const r = await fetch(`${API}/backup.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
    body: JSON.stringify({ action, ...extra }),
  });
  return r.json();
};

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1024 / 1024).toFixed(2) + " MB";
};

// ─── Excel Generator ──────────────────────────────────────────────────────────
const generateExcel = (headers: string[], data: any[], filename: string) => {
  const escapeXml = (v: any) => {
    if (v === null || v === undefined) return "";
    return String(v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  };

  // اعداد به Number تبدیل می‌شن تا SUM و فرمول‌ها در Excel کار کنند
  const getType = (v: any): "Number" | "String" => {
    if (v === null || v === undefined || String(v).trim() === "") return "String";
    const s = String(v).trim().replace(/,/g, "");
    return (s !== "" && !isNaN(Number(s))) ? "Number" : "String";
  };

  const headerRow = headers.map(h => `<Cell ss:StyleID="header"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join("");
  const dataRows = data.map(row =>
    "<Row>" + Object.values(row).map(v => {
      const t = getType(v);
      const val = t === "Number" ? String(v).trim().replace(/,/g, "") : escapeXml(v);
      return `<Cell><Data ss:Type="${t}">${val}</Data></Cell>`;
    }).join("") + "</Row>"
  ).join("\n");

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="header">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#4F46E5" ss:Pattern="Solid"/>
   <Alignment ss:Horizontal="Center"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Sheet1">
  <Table>
   <Row>${headerRow}</Row>
   ${dataRows}
  </Table>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename + ".xls";
  a.click();
  URL.revokeObjectURL(url);
};

export default function BackupPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  const [stats, setStats]         = useState<any>(null);
  const [backups, setBackups]     = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState<string|null>(null);
  const [toast, setToast]         = useState({ text:"", ok:true });
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [backupMode, setBackupMode]         = useState<"full"|"custom">("full");
  const [schedule, setSchedule]             = useState({ enabled: false, time: "03:00", days: "daily" });
  const [scheduleInfo, setScheduleInfo]     = useState("");

  // 🆕 state های گزارش فروشات
  const [salesReportModal, setSalesReportModal] = useState<null | "excel" | "pdf">(null);
  const [salesScope, setSalesScope]             = useState<"all"|"product"|"category">("all");
  const [salesProductIds, setSalesProductIds]   = useState<string[]>([]);  // 🆕 multi-select
  const [salesProductSearch, setSalesProductSearch] = useState("");        // 🆕 جستجو
  const [salesCategoryId, setSalesCategoryId]   = useState("");
  const [salesIncludePending, setSalesIncludePending] = useState(true);
  const [salesFrom, setSalesFrom]               = useState("");
  const [salesTo, setSalesTo]                   = useState("");
  const [productsList, setProductsList]         = useState<any[]>([]);
  const [categoriesList, setCategoriesList]     = useState<any[]>([]);
  const [generatingSales, setGeneratingSales]   = useState(false);

  const showToast = (text:string, ok=true) => { setToast({text,ok}); setTimeout(()=>setToast({text:"",ok:true}),4000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    const [s, b] = await Promise.all([post("get_stats"), post("list_backups")]);
    if (s.success) { setStats(s); setSelectedTables(s.table_stats?.map((t:any)=>t.table)||[]); }
    if (b.success) setBackups(b.backups || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBackupSQL = async () => {
    setExporting("sql");
    try {
      const tables = backupMode === "full" ? undefined : selectedTables;
      const res = await post("backup_sql", tables ? { tables } : {});
      if (res.success) {
        showToast(`بکاپ ساخته شد: ${res.filename}`);
        await loadData();
        const a = document.createElement("a");
        a.href = `https://www.hamidigroupfinance.com${res.url}`;
        a.download = res.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else showToast(res.message || "خطا در ساخت بکاپ", false);
    } catch { showToast("خطا در ارتباط با سرور", false); }
    setExporting(null);
  };

  const handleExportExcel = async (action: string, filename: string) => {
    setExporting(action);
    const res = await post(action);
    if (res.success) {
      generateExcel(res.headers, res.data, filename);
      showToast(`فایل اکسل ${filename} دانلود شد`);
    } else showToast(res.message || "خطا", false);
    setExporting(null);
  };

  const handleDeleteBackup = async (name: string) => {
    if (!window.confirm("آیا مطمئنید؟")) return;
    const res = await post("delete_backup", { name });
    if (res.success) { showToast("بکاپ حذف شد"); loadData(); }
    else showToast("خطا در حذف", false);
  };

  // 🆕 باز کردن مودال گزارش فروشات
  const openSalesReportModal = async (mode: "excel" | "pdf") => {
    setSalesReportModal(mode);
    setSalesScope("all");
    setSalesProductIds([]);
    setSalesProductSearch("");
    setSalesCategoryId("");
    setSalesIncludePending(true);
    setSalesFrom("");
    setSalesTo("");
    // لود محصولات و دسته‌بندی‌ها
    try {
      const [rp, rc] = await Promise.all([
        warehouseAPI.productsList({ status: "active" }),
        warehouseAPI.productCatsList(),
      ]);
      if (rp.success) setProductsList(rp.products || []);
      if (rc.success) setCategoriesList(rc.categories || []);
    } catch {
      showToast("خطا در لود اطلاعات", false);
    }
  };

  // 🆕 تولید گزارش فروشات (Excel یا PDF)
  const handleGenerateSalesReport = async () => {
    if (salesScope === "product" && salesProductIds.length === 0) {
      showToast("لطفاً حداقل یک جنس انتخاب کنید", false);
      return;
    }
    if (salesScope === "category" && !salesCategoryId) {
      showToast("لطفاً یک دسته‌بندی انتخاب کنید", false);
      return;
    }
    setGeneratingSales(true);
    try {
      const r = await fetch(`${API}/warehouse.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
        body: JSON.stringify({
          action: "sales_report",
          scope: salesScope,
          product_ids: salesScope === "product" ? salesProductIds.map(id => parseInt(id)) : [],
          category_id: salesCategoryId ? parseInt(salesCategoryId) : 0,
          include_pending: salesIncludePending,
          from: salesFrom,
          to: salesTo,
        }),
      }).then(res => res.json());
      
      if (!r.success) {
        showToast(r.message || "خطا در دریافت گزارش", false);
        setGeneratingSales(false);
        return;
      }

      // 🛡️ فیلتر دفاعی: اگر scope=product است، فقط محصولات انتخاب شده در گزارش بمانند
      // و grouped را هم باید فیلتر کنیم
      if (salesScope === "product" && salesProductIds.length > 0) {
        const allowedIds = new Set(salesProductIds.map(id => parseInt(id)));
        if (Array.isArray(r.items)) {
          r.items = r.items.filter((it: any) => allowedIds.has(parseInt(it.product_id)));
        }
        if (Array.isArray(r.grouped)) {
          // grouped بر اساس product_name است، باید نام محصولات انتخابی را بگیریم
          const allowedNames = new Set(
            productsList
              .filter((p: any) => allowedIds.has(parseInt(p.id)))
              .map((p: any) => p.name)
          );
          r.grouped = r.grouped.filter((g: any) => allowedNames.has(g.product_name));
        }
        // بازمحاسبه summary
        let totalQty = 0, totalAmount = 0;
        const byProductMap: any = {};
        (r.items || []).forEach((it: any) => {
          totalQty += Number(it.quantity) || 0;
          totalAmount += Number(it.total_price) || 0;
          const pid = it.product_id;
          if (!byProductMap[pid]) {
            byProductMap[pid] = {
              product_id: pid,
              product_name: it.product_name,
              product_code: it.product_code,
              total_qty: 0,
              total_amount: 0,
              currency: it.customer_currency,
            };
          }
          byProductMap[pid].total_qty += Number(it.quantity) || 0;
          byProductMap[pid].total_amount += Number(it.total_price) || 0;
        });
        r.summary = {
          total_qty: totalQty,
          total_amount: totalAmount,
          item_count: (r.items || []).length,
          by_product: Object.values(byProductMap),
        };
      }

      if (salesReportModal === "excel") {
        generateSalesExcel(r);
      } else {
        generateSalesPDF(r);
      }
      showToast(salesReportModal === "excel" ? "اکسل دانلود شد" : "PDF آماده چاپ است");
      setSalesReportModal(null);
    } catch (e: any) {
      showToast("خطا: " + (e?.message || "ناشناخته"), false);
    }
    setGeneratingSales(false);
  };

  // 🆕 تولید Excel گزارش فروشات
  const generateSalesExcel = (data: any) => {
    const items = data.items || [];
    const summary = data.summary || {};
    const ctx = data.context || {};
    const filters = data.filters || {};

    const headers = [
      "ردیف", "تاریخ", "شماره سند", "وضعیت",
      "نام جنس", "کد جنس", "دسته‌بندی",
      "گدام", "شماره موتر", "شماره بارنامه",
      "تعداد/مقدار", "واحد", "قیمت واحد", "قیمت کل", "ارز",
      "نام مشتری", "توضیحات",
    ];

    const rows = items.map((it: any, i: number) => ({
      "ردیف": i + 1,
      "تاریخ": it.sale_date ? gregorianToJalaliStr(it.sale_date) : "",
      "شماره سند": it.sale_serial || "",
      "وضعیت": it.sale_status === "pending" ? "در انتظار" : "تأیید شده",
      "نام جنس": it.product_name || "",
      "کد جنس": it.product_code || "",
      "دسته‌بندی": it.product_category_name || "",
      "گدام": it.warehouse_name || "",
      "شماره موتر": it.truck_plate || "",
      "شماره بارنامه": it.waybill_number || "",
      "تعداد/مقدار": Number(it.quantity) || 0,
      "واحد": it.unit || "",
      "قیمت واحد": Number(it.unit_price) || 0,
      "قیمت کل": Number(it.total_price) || 0,
      "ارز": it.customer_currency || "",
      "نام مشتری": it.customer_name || "",
      "توضیحات": it.sale_notes || "",
    }));

    const escapeXml = (v: any) => {
      if (v === null || v === undefined) return "";
      return String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    };
    const getType = (v: any): "Number" | "String" => {
      if (v === null || v === undefined || String(v).trim() === "") return "String";
      const s = String(v).trim().replace(/,/g, "");
      return (s !== "" && !isNaN(Number(s))) ? "Number" : "String";
    };

    // ===== هدر لوکس =====
    let titleRows = "";

    // عنوان اصلی با gradient effect (در XML با interior color)
    titleRows += `<Row ss:Height="36"><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="bigTitle"><Data ss:Type="String">📊  گزارش جامع فروشات شرکت تجارتی حمیدی گروپ</Data></Cell></Row>`;

    // زیرعنوان
    const subtitle = ctx.scope_label + (ctx.product_names ? " — " + ctx.product_names : "") + (ctx.category_name ? " — " + ctx.category_name : "");
    titleRows += `<Row ss:Height="22"><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="subTitle"><Data ss:Type="String">${escapeXml(subtitle)}</Data></Cell></Row>`;

    // اطلاعات تاریخ
    const rangeText = (filters.from ? "از " + gregorianToJalaliStr(filters.from) : "از ابتدا") + " " + (filters.to ? "تا " + gregorianToJalaliStr(filters.to) : "تا کنون");
    titleRows += `<Row><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="dateInfo"><Data ss:Type="String">📅 بازه: ${escapeXml(rangeText)}     |     🕒 تاریخ تولید: ${escapeXml(gregorianToJalaliStr(new Date().toISOString().slice(0, 10)))}     |     ${filters.include_pending ? "✓ شامل فروشات در انتظار" : "✓ فقط فروشات تأیید شده"}</Data></Cell></Row>`;
    titleRows += `<Row ss:Height="6"></Row>`;

    // ===== کارت‌های خلاصه =====
    titleRows += `<Row ss:Height="26"><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="sectionTitle"><Data ss:Type="String">📈   شاخص‌های کلیدی</Data></Cell></Row>`;

    // ۳ ستون: تعداد ردیف | مجموع تعداد | مجموع مبلغ
    const cnt = Math.floor((headers.length - 1) / 3);
    titleRows += `<Row ss:Height="24">
      <Cell ss:MergeAcross="${cnt}" ss:StyleID="kpiLabel1"><Data ss:Type="String">📋 تعداد ردیف‌های فروش</Data></Cell>
      <Cell ss:MergeAcross="${cnt}" ss:StyleID="kpiLabel2"><Data ss:Type="String">📦 مجموع کل فروش</Data></Cell>
      <Cell ss:MergeAcross="${headers.length - 2 - 2 * cnt - 1}" ss:StyleID="kpiLabel3"><Data ss:Type="String">💰 مجموع مبلغ فروش</Data></Cell>
    </Row>`;
    titleRows += `<Row ss:Height="32">
      <Cell ss:MergeAcross="${cnt}" ss:StyleID="kpiValue1"><Data ss:Type="Number">${summary.item_count || 0}</Data></Cell>
      <Cell ss:MergeAcross="${cnt}" ss:StyleID="kpiValue2"><Data ss:Type="Number">${summary.total_qty || 0}</Data></Cell>
      <Cell ss:MergeAcross="${headers.length - 2 - 2 * cnt - 1}" ss:StyleID="kpiValue3"><Data ss:Type="Number">${summary.total_amount || 0}</Data></Cell>
    </Row>`;
    titleRows += `<Row ss:Height="6"></Row>`;

    // ===== خلاصه به تفکیک کالا =====
    if ((summary.by_product || []).length > 0) {
      titleRows += `<Row ss:Height="26"><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="sectionTitle"><Data ss:Type="String">📦   خلاصه به تفکیک کالا</Data></Cell></Row>`;
      titleRows += `<Row>
        <Cell ss:MergeAcross="3" ss:StyleID="bpHeader"><Data ss:Type="String">نام جنس</Data></Cell>
        <Cell ss:MergeAcross="1" ss:StyleID="bpHeader"><Data ss:Type="String">کد</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="bpHeader"><Data ss:Type="String">مجموع فروش</Data></Cell>
        <Cell ss:MergeAcross="2" ss:StyleID="bpHeader"><Data ss:Type="String">مجموع مبلغ</Data></Cell>
        <Cell ss:MergeAcross="${headers.length - 11}" ss:StyleID="bpHeader"><Data ss:Type="String">ارز</Data></Cell>
      </Row>`;
      summary.by_product.forEach((bp: any, idx: number) => {
        const sid = idx % 2 === 0 ? "bpRowEven" : "bpRowOdd";
        const sidNum = idx % 2 === 0 ? "bpRowEvenNum" : "bpRowOddNum";
        titleRows += `<Row>
          <Cell ss:MergeAcross="3" ss:StyleID="${sid}"><Data ss:Type="String">${escapeXml(bp.product_name)}</Data></Cell>
          <Cell ss:MergeAcross="1" ss:StyleID="${sid}"><Data ss:Type="String">${escapeXml(bp.product_code || "")}</Data></Cell>
          <Cell ss:MergeAcross="2" ss:StyleID="${sidNum}"><Data ss:Type="Number">${bp.total_qty}</Data></Cell>
          <Cell ss:MergeAcross="2" ss:StyleID="${sidNum}"><Data ss:Type="Number">${bp.total_amount}</Data></Cell>
          <Cell ss:MergeAcross="${headers.length - 11}" ss:StyleID="${sid}"><Data ss:Type="String">${escapeXml(bp.currency || "")}</Data></Cell>
        </Row>`;
      });
      titleRows += `<Row ss:Height="6"></Row>`;
    }

    // ===== جدول جزئیات =====
    titleRows += `<Row ss:Height="26"><Cell ss:MergeAcross="${headers.length - 1}" ss:StyleID="sectionTitle"><Data ss:Type="String">📋   جزئیات کامل فروشات</Data></Cell></Row>`;

    const headerRow = `<Row ss:Height="32">${headers.map(h => `<Cell ss:StyleID="header"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join("")}</Row>`;

    const dataRowsXml = rows.map((row: any, idx: number) => {
      const it = items[idx];
      const isPending = it.sale_status === "pending";
      const rowStyle = isPending ? "pendingRow" : (idx % 2 === 0 ? "evenRow" : "oddRow");
      const numStyle = isPending ? "pendingRowNum" : (idx % 2 === 0 ? "evenRowNum" : "oddRowNum");
      const statusStyle = isPending ? "statusPending" : "statusConfirmed";
      return "<Row>" + Object.values(row).map((v: any, ci: number) => {
        const t = getType(v);
        const val = t === "Number" ? String(v).trim().replace(/,/g, "") : escapeXml(v);
        const colName = headers[ci];
        let style = t === "Number" ? numStyle : rowStyle;
        if (colName === "وضعیت") style = statusStyle;
        return `<Cell ss:StyleID="${style}"><Data ss:Type="${t}">${val}</Data></Cell>`;
      }).join("") + "</Row>";
    }).join("\n");

    // ===== فوتر مجموع =====
    const footerRow = `<Row ss:Height="30"><Cell ss:StyleID="totalLabel" ss:MergeAcross="9"><Data ss:Type="String">🧮 جمع کل</Data></Cell>
      <Cell ss:StyleID="totalValue"><Data ss:Type="Number">${summary.total_qty || 0}</Data></Cell>
      <Cell ss:StyleID="totalSpacer"></Cell>
      <Cell ss:StyleID="totalSpacer"></Cell>
      <Cell ss:StyleID="totalValue"><Data ss:Type="Number">${summary.total_amount || 0}</Data></Cell>
      <Cell ss:StyleID="totalSpacer"></Cell>
      <Cell ss:StyleID="totalSpacer"></Cell>
      <Cell ss:StyleID="totalSpacer"></Cell></Row>`;

    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center" ss:Horizontal="Right" ss:ReadingOrder="RightToLeft" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="11"/>
  </Style>

  <Style ss:ID="bigTitle">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft"/>
   <Font ss:FontName="Calibri" ss:Size="18" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#1e3a8a" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="3" ss:Color="#3b82f6"/>
   </Borders>
  </Style>

  <Style ss:ID="subTitle">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft"/>
   <Font ss:FontName="Calibri" ss:Size="13" ss:Bold="1" ss:Color="#1e293b"/>
   <Interior ss:Color="#dbeafe" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="dateInfo">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Italic="1" ss:Color="#475569"/>
   <Interior ss:Color="#f1f5f9" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="sectionTitle">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:Indent="2"/>
   <Font ss:FontName="Calibri" ss:Size="14" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#0f172a" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="4" ss:Color="#0ea5e9"/></Borders>
  </Style>

  <Style ss:ID="kpiLabel1">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#7c3aed" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="kpiLabel2">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#059669" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="kpiLabel3">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#0284c7" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="kpiValue1">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="20" ss:Bold="1" ss:Color="#7c3aed"/>
   <Interior ss:Color="#f5f3ff" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#7c3aed"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#e9d5ff"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#e9d5ff"/>
   </Borders>
  </Style>
  <Style ss:ID="kpiValue2">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="20" ss:Bold="1" ss:Color="#059669"/>
   <Interior ss:Color="#ecfdf5" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#059669"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#a7f3d0"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#a7f3d0"/>
   </Borders>
  </Style>
  <Style ss:ID="kpiValue3">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="20" ss:Bold="1" ss:Color="#0284c7"/>
   <Interior ss:Color="#f0f9ff" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#0284c7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#bae6fd"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#bae6fd"/>
   </Borders>
  </Style>

  <Style ss:ID="bpHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#334155" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
   </Borders>
  </Style>
  <Style ss:ID="bpRowEven">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#1e293b"/>
   <Interior ss:Color="#f8fafc" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#e2e8f0"/></Borders>
  </Style>
  <Style ss:ID="bpRowEvenNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#0ea5e9"/>
   <Interior ss:Color="#f8fafc" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#e2e8f0"/></Borders>
  </Style>
  <Style ss:ID="bpRowOdd">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:Indent="1"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#1e293b"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
   <Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#e2e8f0"/></Borders>
  </Style>
  <Style ss:ID="bpRowOddNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#0ea5e9"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#e2e8f0"/></Borders>
  </Style>

  <Style ss:ID="header">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#1e293b" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#0ea5e9"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#334155"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#334155"/>
   </Borders>
  </Style>

  <Style ss:ID="evenRow">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1e293b"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
   </Borders>
  </Style>
  <Style ss:ID="evenRowNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1e293b"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
   </Borders>
  </Style>
  <Style ss:ID="oddRow">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1e293b"/>
   <Interior ss:Color="#f8fafc" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
   </Borders>
  </Style>
  <Style ss:ID="oddRowNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#1e293b"/>
   <Interior ss:Color="#f8fafc" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#f1f5f9"/>
   </Borders>
  </Style>
  <Style ss:ID="pendingRow">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center" ss:ReadingOrder="RightToLeft" ss:WrapText="1"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Color="#92400e"/>
   <Interior ss:Color="#fef3c7" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
   </Borders>
  </Style>
  <Style ss:ID="pendingRowNum">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#92400e"/>
   <Interior ss:Color="#fef3c7" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Color="#fde68a"/>
   </Borders>
  </Style>
  <Style ss:ID="statusPending">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#f59e0b" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="statusConfirmed">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#10b981" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="totalLabel">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:ReadingOrder="RightToLeft"/>
   <Font ss:FontName="Calibri" ss:Size="13" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#059669" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
   </Borders>
  </Style>
  <Style ss:ID="totalValue">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Calibri" ss:Size="13" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#059669" ss:Pattern="Solid"/>
   <NumberFormat ss:Format="#,##0.###"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
   </Borders>
  </Style>
  <Style ss:ID="totalSpacer">
   <Interior ss:Color="#059669" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#10b981"/>
   </Borders>
  </Style>
 </Styles>
 <Worksheet ss:Name="گزارش فروشات">
  <Table>
   <Column ss:Width="40"/>
   <Column ss:Width="80"/>
   <Column ss:Width="95"/>
   <Column ss:Width="80"/>
   <Column ss:Width="130"/>
   <Column ss:Width="70"/>
   <Column ss:Width="100"/>
   <Column ss:Width="110"/>
   <Column ss:Width="90"/>
   <Column ss:Width="100"/>
   <Column ss:Width="80"/>
   <Column ss:Width="50"/>
   <Column ss:Width="85"/>
   <Column ss:Width="95"/>
   <Column ss:Width="55"/>
   <Column ss:Width="140"/>
   <Column ss:Width="160"/>
   ${titleRows}
   ${headerRow}
   ${dataRowsXml}
   ${footerRow}
  </Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
    <DisplayRightToLeft/>
    <FreezePanes/>
    <FrozenNoSplit/>
    <Print>
      <FitWidth>1</FitWidth>
      <FitHeight>0</FitHeight>
    </Print>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;

    const filename = `sales_report_${ctx.scope || "all"}_${new Date().toISOString().slice(0, 10)}.xls`;
    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 🆕 تولید PDF (با چاپ HTML)
  const generateSalesPDF = (data: any) => {
    const grouped = data.grouped || [];
    const summary = data.summary || {};
    const ctx = data.context || {};
    const filters = data.filters || {};
    const fmt = (n: any) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(Number(n) || 0);
    const escape = (v: any) => v === null || v === undefined ? "" : String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const titleParts = [ctx.scope_label || ""];
    if (ctx.product_names) titleParts.push(ctx.product_names);
    if (ctx.category_name) titleParts.push(ctx.category_name);

    const rangeText = (filters.from ? "از " + gregorianToJalaliStr(filters.from) : "از ابتدا") + " " + (filters.to ? "تا " + gregorianToJalaliStr(filters.to) : "تا کنون");

    // تولید جدول‌های گروه‌بندی شده
    let groupedTablesHtml = "";
    let globalRowIndex = 0;

    grouped.forEach((productGroup: any, pIdx: number) => {
      // هر گروه محصول
      productGroup.warehouses.forEach((whSection: any, wIdx: number) => {
        const itemRowsHtml = whSection.items.map((it: any) => {
          globalRowIndex++;
          const isPending = it.sale_status === "pending";
          return `
            <tr class="${isPending ? 'pending-row' : ''}">
              <td class="td-num">${globalRowIndex}</td>
              <td class="td-date">${escape(gregorianToJalaliStr(it.sale_date || ""))}</td>
              <td class="td-serial">${escape(it.sale_serial)}</td>
              <td class="td-status">
                ${isPending
                  ? `<span class="badge-pending">⏳ در انتظار</span>`
                  : `<span class="badge-confirmed">✓ تأیید</span>`}
              </td>
              <td class="td-notes">${escape(it.sale_notes || "—")}</td>
              <td class="td-truck">${it.truck_plate ? `<span class="badge-truck">🚚 ${escape(it.truck_plate)}</span>` : '<span class="dash">—</span>'}</td>
              <td class="td-waybill">${it.waybill_number ? `<span class="badge-waybill">📄 ${escape(it.waybill_number)}</span>` : '<span class="dash">—</span>'}</td>
              <td class="td-qty">${fmt(it.quantity)}<span class="unit">${escape(it.unit || "")}</span></td>
              <td class="td-price">${fmt(it.unit_price)}</td>
              <td class="td-total">${fmt(it.total_price)}<span class="currency">${escape(it.customer_currency)}</span></td>
              <td class="td-customer">${escape(it.customer_name)}</td>
            </tr>
          `;
        }).join("");

        groupedTablesHtml += `
          <div class="group-section ${wIdx === 0 ? 'group-first' : ''}">
            <div class="group-header">
              <div class="group-title-row">
                <div class="group-icon">🏷️</div>
                <div class="group-info">
                  <div class="group-product">${escape(productGroup.product_name)}</div>
                  <div class="group-warehouse">🏭 ${escape(whSection.warehouse_name)}</div>
                </div>
                <div class="group-meta">
                  <span class="group-meta-item">📋 ${whSection.count} ردیف</span>
                </div>
              </div>
            </div>

            <table class="data-table">
              <thead>
                <tr>
                  <th style="width:35px">#</th>
                  <th style="width:75px">تاریخ</th>
                  <th style="width:95px">شماره سند</th>
                  <th style="width:75px">وضعیت</th>
                  <th>📝 توضیحات</th>
                  <th style="width:85px">🚚 موتر</th>
                  <th style="width:95px">📄 بارنامه</th>
                  <th style="width:90px">تعداد</th>
                  <th style="width:90px">قیمت واحد</th>
                  <th style="width:110px">قیمت کل</th>
                  <th style="width:120px">👤 مشتری</th>
                </tr>
              </thead>
              <tbody>
                ${itemRowsHtml}
              </tbody>
              <tfoot>
                <tr class="subtotal-row">
                  <td colspan="7" class="subtotal-label">جمع ${escape(productGroup.product_name)} — ${escape(whSection.warehouse_name)}</td>
                  <td class="subtotal-val">${fmt(whSection.total_qty)}</td>
                  <td></td>
                  <td class="subtotal-val">${fmt(whSection.total_amount)} <span class="currency-sm">${escape(productGroup.currency)}</span></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        `;
      });

      // مجموع کل محصول
      if (productGroup.warehouses.length > 1) {
        groupedTablesHtml += `
          <div class="product-grand-total">
            <div class="pgt-icon">📊</div>
            <div class="pgt-label">مجموع کل ${escape(productGroup.product_name)} (همه گدام‌ها)</div>
            <div class="pgt-values">
              <span class="pgt-qty">${fmt(productGroup.total_qty)}</span>
              <span class="pgt-sep">|</span>
              <span class="pgt-amount">${fmt(productGroup.total_amount)} ${escape(productGroup.currency)}</span>
            </div>
          </div>
        `;
      }

      // فاصله بین محصولات
      if (pIdx < grouped.length - 1) {
        groupedTablesHtml += `<div class="group-separator"></div>`;
      }
    });

    const html = `<!DOCTYPE html>
<html lang="fa" dir="rtl">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes"/>
<title>گزارش فروشات</title>
<link href="https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 100%; }
  body {
    font-family: 'Vazirmatn','Segoe UI','Tahoma',sans-serif;
    padding: 10px;
    color: #2c3e50;
    font-size: 14px;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    line-height: 1.5;
  }

  /* ===== هدر اداری آرام ===== */
  .header {
    background: #f5f7fa;
    border: 1px solid #d6dde6;
    border-right: 5px solid #3b6491;
    padding: 16px 22px;
    border-radius: 4px;
    margin-bottom: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .header h1 {
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 4px;
  }
  .header .subtitle {
    font-size: 13px;
    color: #5a6c7d;
    font-weight: 500;
  }
  .header .date-box {
    background: #fff;
    padding: 8px 16px;
    border-radius: 4px;
    border: 1px solid #d6dde6;
    text-align: center;
    min-width: 130px;
  }
  .header .date-label {
    font-size: 11px;
    color: #7a8a9a;
    font-weight: 600;
  }
  .header .date-value {
    font-size: 15px;
    font-weight: 700;
    color: #2c3e50;
    margin-top: 2px;
  }

  /* ===== نوار اطلاعات ===== */
  .info-bar {
    background: #fff;
    border: 1px solid #d6dde6;
    padding: 9px 18px;
    margin-bottom: 12px;
    font-size: 12px;
    color: #5a6c7d;
    display: flex;
    gap: 22px;
    align-items: center;
    font-weight: 500;
    border-radius: 4px;
    flex-wrap: wrap;
  }
  .info-bar .item { display: flex; align-items: center; gap: 6px; }
  .info-bar .divider { width: 1px; height: 16px; background: #d6dde6; }
  .info-bar strong { color: #3b6491; font-weight: 700; }

  /* ===== کارت‌های KPI خلاصه — رنگ آرام ===== */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 14px;
  }
  .kpi-card {
    background: #fff;
    border-radius: 4px;
    padding: 12px 18px;
    border: 1px solid #d6dde6;
    border-top: 3px solid #6b8eb0;
  }
  .kpi-card.qty   { border-top-color: #527d3e; }
  .kpi-card.amount{ border-top-color: #3b6491; }
  .kpi-card .label {
    font-size: 12px;
    color: #5a6c7d;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .kpi-card .value {
    font-size: 22px;
    font-weight: 700;
    color: #2c3e50;
    letter-spacing: -0.3px;
  }
  .kpi-card.qty .value { color: #527d3e; }
  .kpi-card.amount .value { color: #3b6491; }

  /* ===== گروه‌های جدول ===== */
  .group-section {
    margin-bottom: 12px;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid #d6dde6;
    background: #fff;
    page-break-inside: avoid;
  }
  .group-header {
    background: #f5f7fa;
    color: #2c3e50;
    padding: 11px 18px;
    border-bottom: 2px solid #3b6491;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .group-icon {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: #3b6491;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }
  .group-info { flex: 1; }
  .group-product {
    font-size: 16px;
    font-weight: 700;
    color: #2c3e50;
  }
  .group-warehouse {
    font-size: 13px;
    color: #5a6c7d;
    margin-top: 2px;
    font-weight: 500;
  }
  .group-meta {
    background: #fff;
    padding: 5px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #5a6c7d;
    border: 1px solid #d6dde6;
  }

  /* ===== جدول داده ===== */
  .data-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
  }
  .data-table thead th {
    background: #2c3e50;
    color: #fff;
    padding: 10px 6px;
    text-align: center;
    font-weight: 700;
    font-size: 13px;
    border-left: 1px solid #34495e;
  }
  .data-table thead th:last-child { border-left: none; }
  .data-table tbody td {
    padding: 8px 8px;
    border-bottom: 1px solid #ecf0f4;
    border-left: 1px solid #f5f7fa;
    vertical-align: middle;
    font-size: 13px;
    line-height: 1.5;
  }
  .data-table tbody td:last-child { border-left: none; }
  .data-table tbody tr:nth-child(even) { background: #fafbfc; }
  .data-table tbody tr.pending-row td { background: #fdf6e3 !important; }

  .td-num { text-align: center; color: #95a5a6; font-weight: 600; }
  .td-date { text-align: center; font-weight: 600; color: #2c3e50; }
  .td-serial { text-align: center; color: #5a6c7d; font-size: 12px; font-weight: 600; }
  .td-status { text-align: center; }
  .td-notes { color: #2c3e50; word-break: break-word; line-height: 1.6; }
  .td-truck, .td-waybill { text-align: center; }
  .td-qty { text-align: center; font-weight: 700; color: #527d3e; font-size: 14px; }
  .td-qty .unit { font-size: 11px; color: #95a5a6; font-weight: 500; display: block; margin-top: 1px; }
  .td-price { text-align: center; color: #2c3e50; font-size: 13px; font-weight: 600; }
  .td-total { text-align: center; font-weight: 700; color: #3b6491; font-size: 14px; }
  .td-total .currency { font-size: 11px; color: #95a5a6; font-weight: 500; display: block; margin-top: 1px; }
  .td-customer { font-weight: 600; color: #2c3e50; }
  .dash { color: #cbd5dc; }

  .badge-pending {
    background: #c19738;
    color: #fff;
    padding: 3px 9px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    display: inline-block;
    white-space: nowrap;
  }
  .badge-confirmed {
    background: #527d3e;
    color: #fff;
    padding: 3px 9px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    display: inline-block;
    white-space: nowrap;
  }
  .badge-truck {
    font-size: 11px;
    color: #3b6491;
    font-weight: 700;
    background: #eef3f8;
    padding: 3px 8px;
    border-radius: 3px;
    border: 1px solid #c5d5e3;
    display: inline-block;
    white-space: nowrap;
  }
  .badge-waybill {
    font-size: 11px;
    color: #4a6c7d;
    font-weight: 700;
    background: #eef4f6;
    padding: 3px 8px;
    border-radius: 3px;
    border: 1px solid #c5dae0;
    display: inline-block;
    white-space: nowrap;
  }

  /* ===== ردیف ساب‌توتال ===== */
  .subtotal-row td {
    background: #3b6491 !important;
    color: #fff !important;
    font-weight: 700 !important;
    padding: 9px 8px !important;
    font-size: 13px !important;
    border: none !important;
  }
  .subtotal-label {
    text-align: right;
    padding-right: 16px !important;
  }
  .subtotal-val {
    text-align: center;
    font-size: 14px !important;
    font-weight: 700;
  }
  .currency-sm { font-size: 11px; opacity: 0.85; font-weight: 500; }

  /* ===== مجموع کل هر محصول ===== */
  .product-grand-total {
    background: #f0f4f8;
    color: #2c3e50;
    padding: 12px 20px;
    border-radius: 4px;
    border: 1px solid #c5d5e3;
    border-right: 4px solid #3b6491;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    page-break-inside: avoid;
  }
  .pgt-icon {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    background: #3b6491;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    flex-shrink: 0;
  }
  .pgt-label { flex: 1; font-size: 14px; font-weight: 700; color: #2c3e50; }
  .pgt-values { display: flex; gap: 18px; align-items: center; }
  .pgt-qty { font-size: 15px; font-weight: 700; color: #527d3e; }
  .pgt-amount { font-size: 15px; font-weight: 700; color: #3b6491; }
  .pgt-sep { color: #95a5a6; }

  .group-separator {
    height: 6px;
  }

  /* ===== جمع کل ===== */
  .final-total {
    background: #2c3e50;
    color: #fff;
    padding: 20px 28px;
    border-radius: 4px;
    margin-top: 18px;
    page-break-inside: avoid;
  }
  .final-total-title {
    font-size: 18px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.2);
  }
  .final-total-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  .final-total-item {
    background: rgba(255,255,255,0.06);
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid rgba(255,255,255,0.15);
    text-align: center;
  }
  .final-total-item .label {
    font-size: 13px;
    opacity: 0.9;
    font-weight: 500;
    margin-bottom: 6px;
  }
  .final-total-item .value {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.3px;
  }

  .footer-note {
    margin-top: 14px;
    padding: 9px 18px;
    background: #f5f7fa;
    border-radius: 4px;
    font-size: 11px;
    color: #7a8a9a;
    text-align: center;
    font-weight: 500;
    border: 1px solid #d6dde6;
  }

  /* ===== موبایل لندسکیپ ===== */
  @media screen and (max-width: 1024px) {
    body { font-size: 13px; padding: 6px; }
    .header { padding: 12px 16px; flex-direction: row; flex-wrap: wrap; gap: 8px; }
    .header h1 { font-size: 17px; }
    .header .subtitle { font-size: 12px; }
    .info-bar { gap: 14px; padding: 8px 12px; font-size: 11px; }
    .info-bar .divider { display: none; }
    .kpi-grid { grid-template-columns: repeat(3, 1fr); gap: 6px; }
    .kpi-card { padding: 10px 12px; }
    .kpi-card .label { font-size: 11px; }
    .kpi-card .value { font-size: 18px; }
    .group-header { padding: 10px 14px; gap: 10px; }
    .group-product { font-size: 14px; }
    .group-warehouse { font-size: 12px; }
    .data-table thead th { font-size: 11px; padding: 8px 4px; }
    .data-table tbody td { font-size: 11px; padding: 6px 5px; }
    .td-qty { font-size: 12px; }
    .td-total { font-size: 12px; }
    .badge-pending, .badge-confirmed { font-size: 10px; padding: 2px 7px; }
    .badge-truck, .badge-waybill { font-size: 10px; padding: 2px 6px; }
    .final-total-title { font-size: 15px; }
    .final-total-item .value { font-size: 20px; }
    .final-total-item .label { font-size: 11px; }
    .product-grand-total { padding: 10px 14px; }
    .pgt-label { font-size: 13px; }
    .pgt-qty, .pgt-amount { font-size: 13px; }
  }

  @media screen and (max-width: 600px) {
    .kpi-grid { grid-template-columns: 1fr; }
    .final-total-grid { grid-template-columns: 1fr; }
    .header { flex-direction: column; align-items: stretch; }
    .header .date-box { text-align: right; }
    .data-table { font-size: 10px; }
    .data-table thead th { font-size: 10px; padding: 6px 3px; }
    .data-table tbody td { font-size: 10px; padding: 5px 4px; }
  }

  @media print {
    .no-print { display: none !important; }
    body { padding: 4px; font-size: 12px; }
  }

  .print-btn {
    position: fixed;
    top: 10px;
    left: 10px;
    padding: 10px 20px;
    background: #3b6491;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    font-family: 'Vazirmatn', sans-serif;
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    z-index: 9999;
  }
  .print-btn:hover { background: #2c4a6e; }
</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">🖨️ چاپ یا ذخیره PDF</button>

<div class="header">
  <div>
    <h1>گزارش جامع فروشات</h1>
    <div class="subtitle">${escape(titleParts.join(" — "))}</div>
  </div>
  <div class="date-box">
    <div class="date-label">تاریخ گزارش</div>
    <div class="date-value">${escape(gregorianToJalaliStr(new Date().toISOString().slice(0, 10)))}</div>
  </div>
</div>

<div class="info-bar">
  <div class="item">بازه: <strong>${escape(rangeText)}</strong></div>
  <div class="divider"></div>
  <div class="item">${filters.include_pending ? "شامل فروشات در انتظار" : "فقط فروشات تأیید شده"}</div>
  <div class="divider"></div>
  <div class="item">شرکت تجارتی حمیدی گروپ</div>
</div>

<div class="kpi-grid">
  <div class="kpi-card">
    <div class="label">تعداد کل ردیف‌های فروش</div>
    <div class="value">${summary.item_count || 0}</div>
  </div>
  <div class="kpi-card qty">
    <div class="label">مجموع کل فروش</div>
    <div class="value">${fmt(summary.total_qty || 0)}</div>
  </div>
  <div class="kpi-card amount">
    <div class="label">مجموع کل مبلغ</div>
    <div class="value">${fmt(summary.total_amount || 0)}</div>
  </div>
</div>

${groupedTablesHtml || '<div style="text-align:center;padding:40px;color:#95a5a6;font-size:14px">هیچ فروشی یافت نشد</div>'}

<div class="final-total">
  <div class="final-total-title">جمع نهایی کل گزارش</div>
  <div class="final-total-grid">
    <div class="final-total-item">
      <div class="label">تعداد ردیف‌های فروش</div>
      <div class="value">${summary.item_count || 0}</div>
    </div>
    <div class="final-total-item">
      <div class="label">مجموع تعداد فروش</div>
      <div class="value">${fmt(summary.total_qty || 0)}</div>
    </div>
    <div class="final-total-item">
      <div class="label">مجموع مبلغ فروش</div>
      <div class="value">${fmt(summary.total_amount || 0)}</div>
    </div>
  </div>
</div>

<div class="footer-note">
  این گزارش توسط سیستم حسابداری حمیدی گروپ تولید شده است
</div>

<script>setTimeout(()=>window.print(),800);</script>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (!w) {
      showToast("لطفاً اجازه باز شدن پنجره را بدهید (Pop-up blocker)", false);
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  const card: React.CSSProperties = {
    background: dark ? "rgba(255,255,255,0.04)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
    borderRadius: 16, padding: "20px 24px",
  };

  const btn = (color: string, outline=false): React.CSSProperties => ({
    display:"flex", alignItems:"center", gap:6, padding:"10px 18px",
    borderRadius:10, border: outline ? `1.5px solid ${color}` : "none",
    background: outline ? "transparent" : color, color: outline ? color : "#fff",
    cursor:"pointer", fontSize:13, fontWeight:700,
    fontFamily:"'Vazirmatn','Segoe UI',sans-serif",
    opacity: exporting ? 0.7 : 1,
  });

  const EXCEL_EXPORTS = [
    { action:"export_customers", label:"مشتریان",    icon:<Users size={15}/>,        filename:"customers",  color:"#ec4899" },
    { action:"export_vendors",   label:"فروشندگان",  icon:<Truck size={15}/>,        filename:"vendors",    color:"#f97316" },
    { action:"export_sales",     label:"فروشات",     icon:<ShoppingCart size={15}/>, filename:"sales",      color:"#10b981" },
    { action:"export_purchases", label:"خریدها",     icon:<Package size={15}/>,      filename:"purchases",  color:"#f59e0b" },
    { action:"export_expenses",  label:"مصارف",      icon:<DollarSign size={15}/>,   filename:"expenses",   color:"#ef4444" },
    { action:"export_accounts",  label:"حساب‌ها",    icon:<BarChart2 size={15}/>,    filename:"accounts",   color:"#3b82f6" },
    { action:"export_employees", label:"کارمندان",   icon:<UserCheck size={15}/>,    filename:"employees",  color:"#8b5cf6" },
    { action:"export_products",  label:"اجناس",      icon:<Shield size={15}/>,       filename:"products",   color:"#06b6d4" },
  ];

  return (
    <div style={{ padding:"28px", fontFamily:"'Vazirmatn','Segoe UI',sans-serif", direction:"rtl" }}>

      {toast.text && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", background:toast.ok?"#10b981":"#ef4444", color:"#fff", padding:"12px 28px", borderRadius:12, fontSize:13, fontWeight:700, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.ok ? <CheckCircle size={14} style={{marginLeft:6}}/> : <AlertCircle size={14} style={{marginLeft:6}}/>}
          {toast.text}
        </div>
      )}

      <div style={{ marginBottom:28 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
          <div style={{ width:44, height:44, borderRadius:13, background:"linear-gradient(135deg,#3b82f6,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <Database size={22} color="#fff"/>
          </div>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:900, color:T.text }}>مرکز بکاپ و خروجی</h1>
            <p style={{ margin:0, fontSize:12, color:T.subtext }}>بکاپ دیتابیس و خروجی اکسل از تمام بخش‌های سیستم</p>
          </div>
        </div>
      </div>

      {stats && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:14, marginBottom:24 }}>
          {[
            { label:"تعداد جداول",  value:stats.tables,      icon:<Table2 size={18}/>,    color:"#3b82f6" },
            { label:"کل رکوردها",   value:stats.total_rows?.toLocaleString(), icon:<Database size={18}/>, color:"#10b981" },
            { label:"حجم دیتابیس", value:fmtSize(stats.size_bytes), icon:<HardDrive size={18}/>, color:"#8b5cf6" },
            { label:"تعداد بکاپ‌ها",value:backups.length,    icon:<Clock size={18}/>,     color:"#f59e0b" },
          ].map((s,i)=>(
            <div key={i} style={{ ...card, display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:`${s.color}18`, display:"flex", alignItems:"center", justifyContent:"center", color:s.color, flexShrink:0 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:11, color:T.subtext }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>

        <div style={{ gridColumn:"1/-1" }}>
          <div style={{ ...card }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <Database size={20} color="#3b82f6"/>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:T.text }}>بکاپ دیتابیس SQL</div>
                  <div style={{ fontSize:11, color:T.subtext }}>خروجی کامل ساختار و داده‌های دیتابیس</div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={loadData} style={btn(T.cardBorder, true)}><RefreshCw size={13}/> بروزرسانی</button>
                <button onClick={handleBackupSQL} disabled={!!exporting} style={btn("linear-gradient(135deg,#3b82f6,#8b5cf6)" as any)}>
                  {exporting==="sql" ? <RefreshCw size={13} style={{animation:"spin 1s linear infinite"}}/> : <Download size={13}/>}
                  {exporting==="sql" ? "در حال ساخت..." : "دریافت بکاپ SQL"}
                </button>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              {[{v:"full",l:"بکاپ کامل (همه جداول)"},{v:"custom",l:"بکاپ سفارشی (انتخاب جداول)"}].map(m=>(
                <button key={m.v} onClick={()=>setBackupMode(m.v as any)} style={{ padding:"8px 16px", borderRadius:9, border:`1.5px solid ${backupMode===m.v?"#3b82f6":T.cardBorder}`, background:backupMode===m.v?"rgba(59,130,246,0.1)":"transparent", color:backupMode===m.v?"#3b82f6":T.text, fontWeight:backupMode===m.v?700:500, cursor:"pointer", fontSize:12, fontFamily:"inherit" }}>{m.l}</button>
              ))}
            </div>
            {stats && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }}>
                {stats.table_stats?.map((t:any) => {
                  const sel = selectedTables.includes(t.table);
                  return (
                    <div key={t.table} onClick={()=>{ if (backupMode==="custom") setSelectedTables(prev => sel ? prev.filter(x=>x!==t.table) : [...prev, t.table]); }} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderRadius:9, border:`1px solid ${sel&&backupMode==="custom"?"#3b82f6":T.cardBorder}`, background:sel&&backupMode==="custom"?"rgba(59,130,246,0.07)":"transparent", cursor:backupMode==="custom"?"pointer":"default" }}>
                      <span style={{ fontSize:12, color:T.text, fontFamily:"monospace" }}>{t.table}</span>
                      <span style={{ fontSize:11, color:T.subtext, background:dark?"rgba(255,255,255,0.06)":"#f1f5f9", padding:"2px 8px", borderRadius:6 }}>{t.rows.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ gridColumn:"1/-1" }}>
          <div style={{ ...card, background: dark ? "linear-gradient(135deg,rgba(16,185,129,0.06),rgba(14,165,233,0.04))" : "linear-gradient(135deg,#f0fdf4,#ecfeff)", border: `1.5px solid ${dark ? "rgba(16,185,129,0.25)" : "#bbf7d0"}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: "linear-gradient(135deg,#10b981,#0ea5e9)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(16,185,129,0.3)" }}>
                <BarChart2 size={20} color="#fff"/>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize:15, fontWeight:800, color:T.text }}>📊 گزارش پیشرفته فروشات</div>
                <div style={{ fontSize:11, color:T.subtext, marginTop: 2 }}>گزارش جامع فروشات با جزئیات کامل: نام جنس، گدام، شماره موتر، تعداد، قیمت، نام مشتری و موجودی گدام بعد از فروش</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <button
                onClick={() => openSalesReportModal("excel")}
                disabled={!!exporting || generatingSales}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "16px 20px", borderRadius: 12,
                  border: "none",
                  background: "linear-gradient(135deg,#10b981,#059669)",
                  color: "#fff", cursor: "pointer",
                  fontSize: 14, fontWeight: 800, fontFamily: "inherit",
                  boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.35)"; }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                  <FileSpreadsheet size={20}/>
                </div>
                <div style={{ textAlign: "right", flex: 1 }}>
                  <div>📈 اکسل فروشات</div>
                  <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, marginTop: 2 }}>دانلود Excel با جزئیات کامل</div>
                </div>
                <Download size={16}/>
              </button>
              <button
                onClick={() => openSalesReportModal("pdf")}
                disabled={!!exporting || generatingSales}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "16px 20px", borderRadius: 12,
                  border: "none",
                  background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
                  color: "#fff", cursor: "pointer",
                  fontSize: 14, fontWeight: 800, fontFamily: "inherit",
                  boxShadow: "0 4px 14px rgba(14,165,233,0.35)",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(14,165,233,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(14,165,233,0.35)"; }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                  <FileText size={20}/>
                </div>
                <div style={{ textAlign: "right", flex: 1 }}>
                  <div>📄 پی دی اف فروشات</div>
                  <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600, marginTop: 2 }}>تولید PDF حرفه‌ای آماده چاپ</div>
                </div>
                <Printer size={16}/>
              </button>
            </div>
          </div>
        </div>

        <div style={{ gridColumn:"1/-1" }}>
          <div style={{ ...card }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <FileSpreadsheet size={20} color="#10b981"/>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:T.text }}>خروجی اکسل</div>
                <div style={{ fontSize:11, color:T.subtext }}>دانلود داده‌های هر بخش به فرمت Excel</div>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10 }}>
              {EXCEL_EXPORTS.map(ex => (
                <button key={ex.action} onClick={()=>handleExportExcel(ex.action, ex.filename)} disabled={!!exporting} style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", borderRadius:12, border:`1.5px solid ${ex.color}30`, background:dark?`${ex.color}10`:`${ex.color}06`, cursor:exporting?"not-allowed":"pointer", fontFamily:"inherit", transition:"all .15s" }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:`${ex.color}18`, display:"flex", alignItems:"center", justifyContent:"center", color:ex.color, flexShrink:0 }}>
                    {exporting===ex.action ? <RefreshCw size={15} style={{animation:"spin 1s linear infinite"}}/> : ex.icon}
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:ex.color }}>{ex.label}</div>
                    <div style={{ fontSize:10, color:T.subtext }}>دانلود Excel</div>
                  </div>
                  <Download size={13} color={ex.color} style={{ marginRight:"auto" }}/>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ gridColumn:"1/-1" }}>
          <div style={{ ...card }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <Clock size={20} color="#f59e0b"/>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:T.text }}>بکاپ‌های قبلی</div>
                <div style={{ fontSize:11, color:T.subtext }}>فایل‌های بکاپ ذخیره شده در سرور</div>
              </div>
            </div>
            {loading ? (
              <div style={{ textAlign:"center", padding:30, color:T.subtext }}>در حال بارگذاری...</div>
            ) : backups.length === 0 ? (
              <div style={{ textAlign:"center", padding:40, color:T.subtext }}>
                <Database size={40} color={T.subtext} style={{ marginBottom:12, opacity:0.5 }}/>
                <div style={{ fontSize:13 }}>هنوز بکاپی گرفته نشده</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {backups.map((b:any) => (
                  <div key={b.name} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderRadius:12, border:`1px solid ${T.cardBorder}`, background:dark?"rgba(255,255,255,0.02)":"#f8fafc" }}>
                    <Database size={18} color="#3b82f6"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:"monospace" }}>{b.name}</div>
                      <div style={{ fontSize:11, color:T.subtext, marginTop:2 }}>{fmtSize(b.size)} — {b.modified}</div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <a href={`https://www.hamidigroupfinance.com${b.url}`} download style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 14px", borderRadius:9, border:"none", background:"rgba(59,130,246,0.12)", color:"#3b82f6", textDecoration:"none", fontSize:12, fontWeight:700, fontFamily:"inherit" }}>
                        <Download size={13}/> دانلود
                      </a>
                      <button onClick={()=>handleDeleteBackup(b.name)} style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 14px", borderRadius:9, border:"none", background:"rgba(239,68,68,0.1)", color:"#ef4444", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit" }}>
                        <Trash2 size={13}/> حذف
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ gridColumn:"1/-1" }}>
          <div style={{ ...card }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <Clock size={20} color="#8b5cf6"/>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:T.text }}>بکاپ خودکار (Schedule)</div>
                <div style={{ fontSize:11, color:T.subtext }}>تنظیم زمان‌بندی برای بکاپ خودکار دیتابیس</div>
              </div>
            </div>
            <div style={{ background:dark?"rgba(139,92,246,0.08)":"#faf5ff", borderRadius:12, padding:20, border:`1px solid ${dark?"rgba(139,92,246,0.2)":"#e9d5ff"}`, marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                <AlertCircle size={15} color="#8b5cf6"/>
                <span style={{ fontSize:12, color:"#8b5cf6", fontWeight:700 }}>راهنمای Cron Job در Hostinger</span>
              </div>
              <div style={{ fontSize:12, color:T.subtext, lineHeight:1.8 }}>
                برای فعال کردن بکاپ خودکار، در Hostinger به <strong style={{color:T.text}}>Hosting → Advanced → Cron Jobs</strong> برو و این دستور را اضافه کن:
              </div>
              <div style={{ background:dark?"rgba(0,0,0,0.4)":"#1e293b", borderRadius:8, padding:"10px 14px", marginTop:10, fontFamily:"monospace", fontSize:12, color:"#10b981", direction:"ltr", textAlign:"left" }}>
                {`0 3 * * * curl -s -X POST https://www.hamidigroupfinance.com/api/backup.php -H "Content-Type: application/json" -d '{"action":"backup_sql"}' > /dev/null 2>&1`}
              </div>
              <div style={{ fontSize:11, color:T.subtext, marginTop:8 }}>این دستور هر روز ساعت ۳ صبح به صورت خودکار بکاپ میگیره.</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              {[
                { label:"هر روز ساعت ۳ صبح", cmd:`0 3 * * *` },
                { label:"هر ۱۲ ساعت یک بار", cmd:`0 */12 * * *` },
                { label:"هر هفته یکشنبه",    cmd:`0 3 * * 0` },
              ].map((s,i)=>(
                <div key={i} style={{ padding:"12px 14px", borderRadius:10, border:`1px solid ${T.cardBorder}`, background:dark?"rgba(255,255,255,0.02)":"#f8fafc" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:6 }}>{s.label}</div>
                  <div style={{ fontFamily:"monospace", fontSize:11, color:"#8b5cf6", direction:"ltr", textAlign:"left" }}>{s.cmd}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 🆕 مودال گزارش فروشات */}
      {salesReportModal && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}
          onClick={() => !generatingSales && setSalesReportModal(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            dir="rtl"
            style={{
              background: dark ? "#1e293b" : "#fff",
              borderRadius: 18,
              maxWidth: 620, width: "100%", maxHeight: "90vh", overflow: "auto",
              border: `1px solid ${T.cardBorder}`,
              boxShadow: "0 25px 70px rgba(0,0,0,0.4)",
              fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            }}
          >
            {/* هدر مودال */}
            <div style={{
              padding: "18px 24px",
              background: salesReportModal === "excel"
                ? "linear-gradient(135deg,#10b981,#059669)"
                : "linear-gradient(135deg,#0ea5e9,#0284c7)",
              color: "#fff",
              borderRadius: "18px 18px 0 0",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }}>
                  {salesReportModal === "excel" ? <FileSpreadsheet size={22}/> : <FileText size={22}/>}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {salesReportModal === "excel" ? "📈 تولید اکسل فروشات" : "📄 تولید PDF فروشات"}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>تنظیمات گزارش را انتخاب کنید</div>
                </div>
              </div>
              <button onClick={() => !generatingSales && setSalesReportModal(null)} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={16}/>
              </button>
            </div>

            {/* بدنه */}
            <div style={{ padding: "20px 24px" }}>
              {/* انتخاب scope */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 10, display: "block" }}>
                  🎯 نوع گزارش
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { v: "all" as const, l: "همه فروشات", icon: "📋" },
                    { v: "product" as const, l: "یک جنس", icon: "📦" },
                    { v: "category" as const, l: "دسته‌بندی", icon: "🏷️" },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setSalesScope(opt.v)}
                      style={{
                        padding: "12px 10px", borderRadius: 10,
                        border: `2px solid ${salesScope === opt.v ? (salesReportModal === "excel" ? "#10b981" : "#0ea5e9") : T.cardBorder}`,
                        background: salesScope === opt.v
                          ? (salesReportModal === "excel" ? "rgba(16,185,129,0.1)" : "rgba(14,165,233,0.1)")
                          : "transparent",
                        color: salesScope === opt.v ? (salesReportModal === "excel" ? "#10b981" : "#0ea5e9") : T.text,
                        cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.icon}</div>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* انتخاب جنس — چک‌باکس چندگانه */}
              {salesScope === "product" && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 800, color: T.text, marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>📦 انتخاب اجناس (چند انتخابی)</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: salesProductIds.length > 0 ? "#10b981" : T.subtext,
                      padding: "3px 10px", borderRadius: 8,
                      background: salesProductIds.length > 0 ? "rgba(16,185,129,0.12)" : (dark ? "rgba(255,255,255,0.05)" : "#f1f5f9"),
                    }}>
                      {salesProductIds.length > 0 ? `✓ ${salesProductIds.length} جنس انتخاب شده` : "هیچ‌کدام انتخاب نشده"}
                    </span>
                  </label>

                  {/* جستجو */}
                  <input
                    value={salesProductSearch}
                    onChange={e => setSalesProductSearch(e.target.value)}
                    placeholder="🔍 جستجوی نام یا کد جنس..."
                    style={{
                      width: "100%",
                      padding: "9px 14px",
                      borderRadius: 9,
                      border: `1.5px solid ${T.cardBorder}`,
                      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
                      color: T.text,
                      fontSize: 12.5,
                      fontFamily: "inherit",
                      direction: "rtl" as const,
                      outline: "none",
                      marginBottom: 8,
                      boxSizing: "border-box" as const,
                    }}
                  />

                  {/* دکمه‌های انتخاب همه / لغو */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={() => {
                        const filteredIds = productsList
                          .filter((p: any) => {
                            if (!salesProductSearch) return true;
                            return p.name.toLowerCase().includes(salesProductSearch.toLowerCase())
                                || (p.code || "").toLowerCase().includes(salesProductSearch.toLowerCase());
                          })
                          .map((p: any) => String(p.id));
                        setSalesProductIds(filteredIds);
                      }}
                      style={{
                        padding: "5px 12px", borderRadius: 7,
                        border: "1px solid #10b981", background: "transparent",
                        color: "#10b981", cursor: "pointer",
                        fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      ✓ انتخاب همه نتایج
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalesProductIds([])}
                      style={{
                        padding: "5px 12px", borderRadius: 7,
                        border: `1px solid ${T.cardBorder}`, background: "transparent",
                        color: T.subtext, cursor: "pointer",
                        fontSize: 11, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      ✗ لغو همه
                    </button>
                  </div>

                  {/* لیست اجناس با چک‌باکس */}
                  <div style={{
                    maxHeight: 240,
                    overflowY: "auto" as const,
                    border: `1.5px solid ${T.cardBorder}`,
                    borderRadius: 10,
                    background: dark ? "rgba(255,255,255,0.02)" : "#fafbfc",
                  }}>
                    {(() => {
                      const filtered = productsList.filter((p: any) => {
                        if (!salesProductSearch) return true;
                        return p.name.toLowerCase().includes(salesProductSearch.toLowerCase())
                            || (p.code || "").toLowerCase().includes(salesProductSearch.toLowerCase());
                      });
                      if (filtered.length === 0) {
                        return <div style={{ textAlign: "center" as const, padding: 24, color: T.subtext, fontSize: 12 }}>هیچ جنسی یافت نشد</div>;
                      }
                      return filtered.map((p: any) => {
                        const id = String(p.id);
                        const checked = salesProductIds.includes(id);
                        return (
                          <label
                            key={id}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 14px",
                              borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.03)" : "#f1f5f9"}`,
                              cursor: "pointer",
                              background: checked ? (dark ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.06)") : "transparent",
                              transition: "background 0.1s",
                            }}
                            onMouseEnter={e => { if (!checked) e.currentTarget.style.background = dark ? "rgba(255,255,255,0.03)" : "#f1f5f9"; }}
                            onMouseLeave={e => { if (!checked) e.currentTarget.style.background = "transparent"; }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSalesProductIds([...salesProductIds, id]);
                                } else {
                                  setSalesProductIds(salesProductIds.filter(x => x !== id));
                                }
                              }}
                              style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#10b981", flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{p.name}</div>
                              <div style={{ fontSize: 10, color: T.subtext, marginTop: 2, fontFamily: "monospace" }}>{p.code || ""}</div>
                            </div>
                            {checked && <CheckCircle size={14} color="#10b981" style={{ flexShrink: 0 }}/>}
                          </label>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* انتخاب دسته‌بندی */}
              {salesScope === "category" && (
                <div style={{ marginBottom: 16 }}>
                  <SearchableSelect
                    label="🏷️ انتخاب دسته‌بندی"
                    options={categoriesList.map((c: any) => ({ value: String(c.id), label: (c.parent_id ? "↳ " : "") + c.name }))}
                    value={salesCategoryId}
                    onChange={v => setSalesCategoryId(v)}
                    placeholder="دسته‌بندی مورد نظر را انتخاب کنید..."
                  />
                </div>
              )}

              {/* فیلتر تاریخ */}
              <div style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 12, background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc", border: `1px dashed ${T.cardBorder}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.subtext, marginBottom: 10 }}>📅 بازه زمانی (اختیاری — خالی بگذارید برای همه فروشات)</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <PersianDatePicker label="از تاریخ" value={salesFrom} onChange={v => setSalesFrom(v)} />
                  <PersianDatePicker label="تا تاریخ" value={salesTo} onChange={v => setSalesTo(v)} />
                </div>
              </div>

              {/* تنظیمات */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.08)" : "#fffbeb", border: `1px solid ${dark ? "rgba(245,158,11,0.2)" : "#fde68a"}` }}>
                  <input
                    type="checkbox"
                    checked={salesIncludePending}
                    onChange={e => setSalesIncludePending(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#f59e0b" }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>⏳ شامل فروشات "در انتظار" (pending)</div>
                    <div style={{ fontSize: 10, color: T.subtext, marginTop: 2 }}>اگه فعال باشد، فروشات تأیید نشده هم در گزارش می‌آیند و با برچسب "در انتظار" مشخص می‌شوند</div>
                  </div>
                </label>
              </div>

              {/* دکمه‌ها */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.cardBorder}` }}>
                <button
                  onClick={() => setSalesReportModal(null)}
                  disabled={generatingSales}
                  style={{
                    padding: "10px 18px", borderRadius: 9,
                    border: `1.5px solid ${T.cardBorder}`,
                    background: "transparent", color: T.text,
                    cursor: generatingSales ? "not-allowed" : "pointer",
                    fontSize: 13, fontFamily: "inherit", fontWeight: 700,
                  }}
                >
                  انصراف
                </button>
                <button
                  onClick={handleGenerateSalesReport}
                  disabled={generatingSales}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "10px 22px", borderRadius: 9, border: "none",
                    background: generatingSales
                      ? "#94a3b8"
                      : (salesReportModal === "excel" ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#0ea5e9,#0284c7)"),
                    color: "#fff",
                    cursor: generatingSales ? "not-allowed" : "pointer",
                    fontSize: 13, fontFamily: "inherit", fontWeight: 800,
                    boxShadow: generatingSales ? "none" : `0 4px 14px ${salesReportModal === "excel" ? "rgba(16,185,129,0.4)" : "rgba(14,165,233,0.4)"}`,
                  }}
                >
                  {generatingSales ? (
                    <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }}/> در حال تولید...</>
                  ) : (
                    <>{salesReportModal === "excel" ? <FileSpreadsheet size={14}/> : <Printer size={14}/>} {salesReportModal === "excel" ? "تولید اکسل" : "تولید PDF"}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}